import { produce } from '@/globals/helpers/immer';
import {
	WebAPIDataEntryItem,
	WebAPIDataEntryList,
	WebAPILineGraphChartSlicerData,
} from '../../../../globals/webApiTypes/elements/WebAPICBS';
import { ValuesByDimension } from '../lineGraph.types';

const addValueForTopicKeyToMemo =
	({
		periodDate,
		dataEntry,
	}: {
		periodDate: Date;
		dataEntry: WebAPIDataEntryItem;
	}) =>
	(memo: ValuesByDimension, topicKey: string) => {
		const value = dataEntry.values[topicKey];

		if (value == null) return memo;

		return produce(memo, (draft) => {
			((draft || {})[topicKey] ??= {})[periodDate.toString()] = value;
			return;
		});
	};

const addValueForCategoryKeyToMemo =
	({
		dataEntry,
		periodDate,
		topicKey,
		categoryKey,
	}: {
		dataEntry: WebAPIDataEntryItem;
		periodDate: Date;
		topicKey: string;
		categoryKey: string;
	}) =>
	(memo: ValuesByDimension) => {
		const value = dataEntry.values[topicKey];

		if (value == null) return memo;

		return produce(memo, (draft) => {
			((draft || {})[categoryKey] ??= {})[periodDate.toString()] = value;
			return;
		});
	};

export const arrangeValuesByDimension = ({
	dataEntryList,
	multiDimension,
	topicKeys,
}: {
	dataEntryList: WebAPIDataEntryList;
	multiDimension: WebAPILineGraphChartSlicerData['multiDimension'];
	topicKeys: WebAPILineGraphChartSlicerData['topicKeys'];
}) => {
	if (!multiDimension) {
		const topicKey = topicKeys[0];

		const valuesByDimension = dataEntryList.reduce((memo, dataEntry) => {
			const { periodDate } = dataEntry;

			return addValueForTopicKeyToMemo({ periodDate, dataEntry })(
				memo,
				topicKey,
			);
		}, {} as ValuesByDimension);

		return valuesByDimension;
	}

	if (multiDimension === 'DIMENSION_TOPIC') {
		const valuesByDimension = dataEntryList.reduce((memo, dataEntry) => {
			const { periodDate } = dataEntry;

			return topicKeys.reduce(
				addValueForTopicKeyToMemo({ periodDate, dataEntry }),
				memo,
			);
		}, {} as ValuesByDimension);

		return valuesByDimension;
	}

	const topicKey = topicKeys[0];
	const valuesByDimension = dataEntryList.reduce((memo, dataEntry) => {
		const { periodDate } = dataEntry;
		const categoryKey = dataEntry.categories[multiDimension];

		return addValueForCategoryKeyToMemo({
			dataEntry,
			categoryKey,
			periodDate,
			topicKey,
		})(memo);
	}, {} as ValuesByDimension);

	return valuesByDimension;
};
